// components/FieldError.tsx
import React from "react";
import { useProfileStore } from "@pages/ClientProfile/store/profileStore.ts";
import { AppTooltip } from "@components/Tooltip/AppTooltip.tsx";
import type { OCRError } from "@pages/ClientProfile/types.ts";

interface FieldErrorProps {
  fieldKey: string;
  fieldType?: string;
  onDoubleClick?: (e: React.MouseEvent) => void;
  className?: string;
  children: React.ReactNode;
}

export const FieldError: React.FC<FieldErrorProps> = ({
  fieldKey,
  fieldType,
  onDoubleClick,
  className,
  children,
}) => {
  const { diagnosticErrors } = useProfileStore();

  const fieldErrors = diagnosticErrors[fieldKey] ?? [];
  if (fieldErrors.length === 0)
    return (
      <div
        id={`review-item-${fieldKey}`}
        className={className}
        onDoubleClick={onDoubleClick}
      >
        {children}
      </div>
    );

  // Check if any errors are OCR errors with suggestions
  const hasOCRErrorWithSuggestion = fieldErrors.some(
    (error): error is OCRError =>
      error.type === "ocr_error" && Boolean((error as OCRError).suggested)
  );

  const classNames = `${className}`.trim();

  // If there's an OCR error with suggestion, don't show the tooltip
  if (hasOCRErrorWithSuggestion) {
    return (
      <div
        className={classNames}
        id={`review-item-${fieldKey}`}
        onDoubleClick={onDoubleClick}
      >
        {children}
      </div>
    );
  }

  return (
    <AppTooltip
      color="#B02C2E" // softer red
      textColor="#fff"
      labels={fieldErrors.map((err) => err.error)}
      position={
        fieldType === "select" || fieldType === "multiselect" ? "right" : "top"
      }
    >
      <div
        className={classNames}
        id={`review-item-${fieldKey}`}
        onDoubleClick={onDoubleClick}
      >
        {children}
      </div>
    </AppTooltip>
  );
};
